import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SignOut, ArrowLeft, MapPin, FolderOpen, Buildings } from "@phosphor-icons/react";
import api, { fileUrl } from "@/lib/api";
import { eur, price, num } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import LanguageSelector from "@/components/LanguageSelector";
import { StatusBadge, Field } from "@/components/common";
import { localizedField } from "@/lib/localized";


function PortalHeader({ onBack }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="bg-[#0B2545] text-white">
      <div className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-4">
        <img src="/panatlantik-logo.png" alt="" className="w-11 h-11 object-contain" />
        <div className="min-w-0 flex-1">
          <div className="font-serif text-lg leading-tight">{t("panatlantikInternational", "PANATLANTIK INTERNATIONAL")}</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{t("portal.title", "Client Portal")}</div>
        </div>
        {onBack && (
          <button onClick={onBack} data-testid="portal-back" className="text-sm text-slate-300 hover:text-white flex items-center gap-1">
            <ArrowLeft size={16} /> {t("portal.back", "All properties")}
          </button>
        )}
        <span className="hidden sm:block text-sm text-slate-300 truncate max-w-[160px]" data-testid="portal-user">{user?.name}</span>
        <div className="w-32"><LanguageSelector compact /></div>
        <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="portal-logout" className="text-slate-300 hover:text-white" title={t("navigation.signOut", "Sign out")}>
          <SignOut size={20} />
        </button>
      </div>
    </header>
  );
}

function PropertyCard({ p, onOpen }) {
  const { t, i18n } = useTranslation();
  return (
    <button onClick={onOpen} data-testid={`portal-card-${p.id}`} className="text-left bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-md transition-shadow group">
      <div className="h-44 bg-slate-100 overflow-hidden">
        {p.cover_image ? <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
          : <div className="w-full h-full flex items-center justify-center text-slate-300"><Buildings size={36} /></div>}
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-xs text-slate-400">{p.reference}</span>
          <StatusBadge status={p.status} />
        </div>
        <h3 className="font-medium text-slate-900 truncate">{localizedField(p, "title", i18n)}</h3>
        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={12} />{[p.city, p.district, p.country].filter(Boolean).join(", ")}</div>
        <div className="font-serif text-xl text-[#0B2545] mt-3">{price(p.asking_price)}</div>
        <div className="text-xs text-slate-400 mt-1">{t(p.category, p.category)}{p.gross_area ? ` · ${t("valM", "{{val}} m²", { val: num(p.gross_area) })}` : ""}</div>
      </div>
    </button>
  );
}

function PropertyView({ id }) {
  const { t, i18n } = useTranslation();
  const { data: p, isLoading, error } = useQuery({ queryKey: ["portal-property", id], queryFn: async () => (await api.get(`/portal/properties/${id}`)).data });
  if (isLoading) return <p className="text-sm text-slate-400 py-10">{t("loading", "Loading…")}</p>;
  if (error || !p) return <p className="text-sm text-red-600 py-10" data-testid="portal-not-found">{t("portal.notAvailable", "This property is not available to you.")}</p>;
  const media = p.media || [];
  const photos = [...(p.cover_image ? [p.cover_image] : []), ...(p.gallery || []),
    ...media.filter((m) => (m.content_type || "").startsWith("image")).map((m) => fileUrl(m.id))];
  const documents = media.filter((m) => !(m.content_type || "").startsWith("image"));
  return (
    <div data-testid="portal-property">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="text-xs text-slate-400">{p.reference}</div>
          <h1 className="font-serif text-3xl text-[#0B2545]">{localizedField(p, "title", i18n)}</h1>
          <div className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin size={14} />{[p.address, p.city, p.district, p.country].filter(Boolean).join(", ")}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wide text-slate-400">{t("askingPrice", "Asking Price")}</div>
          <div className="font-serif text-3xl text-[#0B2545]">{price(p.asking_price)}</div>
        </div>
      </div>
      {photos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {photos.slice(0, 8).map((u, i) => (
            <img key={i} src={u} alt="" className={`${i === 0 ? "col-span-2 row-span-2 h-full" : "h-32"} w-full object-cover rounded-sm bg-slate-100`} />
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="font-serif text-xl mb-3">{t("description", "Description")}</h3>
          <p className="text-slate-600 leading-relaxed whitespace-pre-line">{localizedField(p, "description", i18n) || t("noDescriptionProvided", "No description provided.")}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mt-6 pt-6 border-t border-slate-100">
            <Field label={t("category", "Category")}>{p.category || "—"}</Field>
            <Field label={t("bedrooms", "Bedrooms")}>{p.bedrooms || "—"}</Field>
            <Field label={t("bathrooms", "Bathrooms")}>{p.bathrooms || "—"}</Field>
            <Field label={t("grossArea", "Gross area")}>{p.gross_area ? t("valM", "{{val}} m²", { val: num(p.gross_area) }) : "—"}</Field>
            <Field label={t("landArea", "Land area")}>{p.land_area ? t("valM", "{{val}} m²", { val: num(p.land_area) }) : "—"}</Field>
            <Field label={t("yearBuilt", "Year built")}>{p.year_built || "—"}</Field>
            <Field label={t("condition", "Condition")}>{p.condition || "—"}</Field>
            <Field label={t("estimatedRoi", "Estimated ROI")}>{p.estimated_roi ? `${p.estimated_roi}%` : "—"}</Field>
            <Field label={t("developmentPotential", "Development potential")}>{localizedField(p, "development_potential", i18n) || "—"}</Field>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="font-serif text-lg mb-3">{t("documentsFiles", "Documents & Files")}</h3>
          {documents.length === 0 ? <p className="text-sm text-slate-400" data-testid="portal-no-docs">{t("portal.noDocuments", "No documents shared yet.")}</p> : (
            <div className="space-y-2">
              {documents.map((d) => (
                <a key={d.id} href={fileUrl(d.id)} target="_blank" rel="noreferrer" data-testid={`portal-doc-${d.id}`} className="flex items-center gap-3 p-3 border border-slate-100 rounded-sm hover:bg-slate-50">
                  <FolderOpen size={20} className="text-[#C5A059]" />
                  <span className="text-sm text-slate-700 flex-1 truncate">{d.original_filename}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Portal() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const { data: props = [], isLoading } = useQuery({ queryKey: ["portal-properties"], queryFn: async () => (await api.get("/portal/properties")).data });

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="portal-page">
      <PortalHeader onBack={selected ? () => setSelected(null) : null} />
      <main className="max-w-6xl mx-auto px-5 py-8">
        {selected ? <PropertyView id={selected} /> : (
          <>
            <h1 className="font-serif text-3xl text-[#0B2545]">{t("portal.heading", "Properties shared with you")}</h1>
            <p className="text-sm text-slate-500 mt-1 mb-8">{t("portal.subheading", "A private selection prepared by your Panatlantik consultant. Read-only access.")}</p>
            {isLoading ? <p className="text-sm text-slate-400">{t("loading", "Loading…")}</p>
              : props.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-12 text-center" data-testid="portal-empty">
                  <Buildings size={36} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">{t("portal.empty", "Nothing has been shared with you yet. Your consultant will notify you when new opportunities are available.")}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="portal-grid">
                  {props.map((p) => <PropertyCard key={p.id} p={p} onOpen={() => setSelected(p.id)} />)}
                </div>
              )}
          </>
        )}
      </main>
    </div>
  );
}
